import { IntlMessageFormat } from "intl-messageformat";
import errorsUk from "../../../../i18n/uk/errors.properties?raw";
import uk from "../../../../i18n/uk/web.json";

/*
 * Строки интерфейса ключами ICU, основной каталог i18n/uk/web.json. Ключ
 * проверяет компилятор: несуществующий не соберётся. Каталоги ru и en
 * появятся позже, тогда язык придёт от пользователя.
 */
export type MessageKey = keyof typeof uk;

const LOCALE = "uk";
const catalog: Record<string, string> = uk;
const cache = new Map<string, IntlMessageFormat>();

export function t(key: MessageKey, values?: Record<string, string | number>): string {
  let format = cache.get(key);
  if (!format) {
    format = new IntlMessageFormat(catalog[key] ?? key, LOCALE);
    cache.set(key, format);
  }
  return String(format.format(values));
}

/** Разбор .properties: ключ=значение, # и ! комментарии. Экранирования в каталоге нет. */
export function parseProperties(text: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("#") || line.startsWith("!")) continue;
    const eq = line.indexOf("=");
    if (eq > 0) out.set(line.slice(0, eq).trim(), line.slice(eq + 1).trim());
  }
  return out;
}

/*
 * Тексты ошибок сервера по коду правила (i18n/uk/errors.properties, его же
 * читает сервер для problem+json). Веб берёт оттуда отказы входа: код
 * приходит в ?login_error, текста в ответе нет.
 */
const serverErrors = parseProperties(errorsUk);

export function serverErrorText(code: string, values?: Record<string, string | number>): string | undefined {
  const message = serverErrors.get(code);
  if (message === undefined) return undefined;
  try {
    return String(new IntlMessageFormat(message, LOCALE).format(values));
  } catch {
    // Сервер не прислал аргумент, которого ждёт текст: лучше detail ответа.
    return undefined;
  }
}

/** Числа и количества по-украински: пробел тысяч, запятая. */
const numberFormat = new Intl.NumberFormat(LOCALE);
export const formatNumber = (n: number): string => numberFormat.format(n);

// Деньги: две цифры после запятой, гривня словом «грн», валюта кодом
// (1 000,00 USD). Значок ₴ на чеках и в рахунках ЧМК не пишут.
const moneyFormat = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const formatMoney = (n: number, currency: string): string =>
  `${moneyFormat.format(n)}\u00a0${currency === "UAH" ? "грн" : currency}`;

// Дата документа «10.11» без года: склад живёт неделями, год только шумит.
// Строка ISO из ответа это календарная дата, без часового пояса.
const dateFormat = new Intl.DateTimeFormat(LOCALE, { day: "2-digit", month: "2-digit", timeZone: "UTC" });
export const formatDate = (iso: string): string => dateFormat.format(new Date(`${iso}T00:00:00Z`));
